class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');

    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );

      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  /*
   * Remove title: if it somehow already exists.
   */
  cleanSearchTerm(searchTerm) {
    return searchTerm
      .replace(/^title:/i, '')
      .replace(/"/g, '')
      .trim();
  }

  /*
   * Convert normal customer search into title-only search.
   *
   * Example:
   * unisex -> title:unisex
   * chand  -> title:chand
   */
  getTitleSearchQuery(searchTerm) {
    const cleanTerm = this.cleanSearchTerm(searchTerm);

    if (!cleanTerm.length) {
      return '';
    }

    return 'title:' + cleanTerm;
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  /*
   * NORMAL SEARCH / ENTER
   *
   * This is important.
   *
   * Instead of allowing the old theme search to submit
   * the normal query, we directly send the title-only query.
   */
  onFormSubmit(event) {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      event.preventDefault();
      return;
    }

    /*
     * If a predictive product is selected,
     * keep the existing behavior.
     */
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) {
      event.preventDefault();
      selectedProduct.click();
      return;
    }

    /*
     * No predictive product selected.
     *
     * Perform title-only search directly.
     */
    event.preventDefault();

    const titleQuery = this.getTitleSearchQuery(searchTerm);

    if (!titleQuery.length) {
      return;
    }

    const searchUrl = new URL(
      window.routes && window.routes.search_url
        ? window.routes.search_url
        : '/search',
      window.location.origin
    );

    searchUrl.searchParams.set('q', titleQuery);
    searchUrl.searchParams.set('type', 'product');

    window.location.href = searchUrl.toString();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    /*
     * Do NOT prevent the Enter key here.
     *
     * The old code was calling preventDefault()
     * for every key, including Enter.
     *
     * That can interfere with normal form submission.
     */
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;

      case 'Enter':
        /*
         * Enter is handled by the form submit event.
         */
        break;
    }
  }

  onKeydown(event) {
    /*
     * Prevent the cursor from moving in the input
     * when using the up/down arrow keys.
     */
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) {
      return;
    }

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');

    let activeElement = this.querySelector('li');

    if (!activeElement) {
      return;
    }

    if (moveUp && !selectedElement) {
      return;
    }

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (!activeElement || activeElement === selectedElement) {
      return;
    }

    activeElement.setAttribute('aria-selected', true);

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', false);
    }

    this.setLiveRegionText(activeElement.textContent);

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    /*
     * Clean customer input.
     */
    const cleanTerm = this.cleanSearchTerm(searchTerm);

    if (!cleanTerm.length) {
      this.close(true);
      return;
    }

    /*
     * IMPORTANT:
     *
     * Predictive search now searches ONLY product titles.
     *
     * Example:
     * unisex
     * becomes:
     * title:unisex
     */
    const titleQuery = this.getTitleSearchQuery(cleanTerm);

    /*
     * Cache based on the title-only query.
     */
    const queryKey = titleQuery
      .replace(/\s+/g, '-')
      .toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const predictiveSearchUrl =
      `${routes.predictive_search_url}?q=${encodeURIComponent(titleQuery)}` +
      `&${encodeURIComponent('resources[type]')}=product` +
      `&${encodeURIComponent('resources[limit]')}=4` +
      `&section_id=predictive-search`;

    fetch(predictiveSearchUrl)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!section) {
          this.close();
          return;
        }

        const resultsMarkup = section.innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text');

    if (this.statusElement) {
      this.setLiveRegionText(this.loadingText);
    }

    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute(
          'aria-hidden',
          'true'
        );
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.setAttribute('results', true);

    this.setLiveRegionResults();

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    const headerSection = document.querySelector('.section-header');

    if (!headerSection) {
      return window.innerHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      headerSection.getBoundingClientRect().bottom;

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', true);

    this.input.setAttribute(
      'aria-expanded',
      true
    );

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';

      this.removeAttribute('results');
    }

    const selected = this.querySelector(
      '[aria-selected="true"]'
    );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute('open');

    this.input.setAttribute(
      'aria-expanded',
      false
    );

    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }

    this.isOpen = false;
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);